import type { ContentfulAsset, ContentfulBynderImage } from '@reward-platform/contentful'

export type ContentfulImageFormat = 'jpg' | 'webp' | 'png' | 'gif' | 'avif'

export type ContentfulImageSize = {
  width: number
  height: number
  media?: string
  densities?: number[]
}

type ContentfulImagePropsSource = {
  bynderImage?: ContentfulBynderImage[]
  image?: ContentfulAsset
  fallbackAlt?: string
}

const contentfulImageFormats = ['jpg', 'webp', 'png', 'gif', 'avif'] as const

export const getContentTypeFromImageFormat = (
  format?: ContentfulImageFormat
): string | undefined => {
  if (!format) {
    return undefined
  }

  const contentTypeFormat = format === 'jpg' ? 'jpeg' : format

  return `image/${contentTypeFormat}`
}

const normaliseImageFormat = (format?: string | null): ContentfulImageFormat | undefined => {
  const normalisedFormat = format?.toLowerCase() === 'jpeg' ? 'jpg' : format?.toLowerCase()

  return contentfulImageFormats.find((item) => item === normalisedFormat)
}

const getImageFormatFromContentType = (contentType?: string): ContentfulImageFormat | undefined =>
  normaliseImageFormat(contentType?.split('/').at(-1))

const getImageFormatFromUrl = (url?: string | null): ContentfulImageFormat | undefined => {
  if (!url) {
    return undefined
  }

  const extension = url.split('?')[0]?.split('.').at(-1)
  return normaliseImageFormat(extension)
}

const getBynderImageUrl = (image?: ContentfulBynderImage): string | undefined =>
  image?.selectedFile?.url ?? image?.src

const getBynderImageFormat = (image?: ContentfulBynderImage): ContentfulImageFormat | undefined =>
  normaliseImageFormat(image?.extension?.[0]) ?? getImageFormatFromUrl(getBynderImageUrl(image))

const getBynderImageContentType = (image?: ContentfulBynderImage): string | undefined => {
  if (image?.type?.includes('/')) {
    return image.type
  }

  return getContentTypeFromImageFormat(getBynderImageFormat(image))
}

export const getContentfulImageProps = ({
  bynderImage,
  image,
  fallbackAlt = '',
}: ContentfulImagePropsSource) => {
  const selectedBynderImage = bynderImage?.find((item) => getBynderImageUrl(item))
  const bynderImageUrl = getBynderImageUrl(selectedBynderImage)

  return {
    imgSrc: bynderImageUrl ?? image?.file?.url ?? '',
    imgAlt:
      selectedBynderImage?.description ?? selectedBynderImage?.name ?? image?.title ?? fallbackAlt,
    imgFormat:
      getBynderImageFormat(selectedBynderImage) ??
      getImageFormatFromContentType(image?.file?.contentType),
    imgContentType: getBynderImageContentType(selectedBynderImage) ?? image?.file?.contentType,
  }
}

function normalizeContentfulUrl(url: string | undefined): string {
  if (!url) {
    return ''
  }
  if (url.startsWith('//')) {
    return `https:${url}`
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
    return url
  }
  return `https://${url}`
}

export function formatContentfulImageSrc(
  url: string | undefined,
  size: ContentfulImageSize,
  format: ContentfulImageFormat = 'jpg'
): string {
  const normalizedUrl = normalizeContentfulUrl(url)
  const { width, height, densities } = size

  if (densities) {
    return densities
      .map(
        (dpi) =>
          `${formatContentfulImageSrc(
            normalizedUrl,
            {
              width: width * dpi,
              height: height * dpi,
            },
            format
          )} ${dpi}x`
      )
      .join(', ')
  }

  const fm = format === 'jpg' ? 'fm=jpg&q=90&fl=progressive' : `fm=${format}`
  return `${normalizedUrl}?${fm}&fit=fill&w=${width}&h=${height}`
}
